import { useCallback, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import { DeleteDialogCustomField } from 'common/components/deleteDialog/DeleteDialogCustomField';
import {
    selectCurrentEntityType,
    selectDeleteCurrentTenantId,
    setCurrentEntityType,
    setDialogOpen
} from 'common/components/deleteDialog/deleteDialogSlice';
import { deleteUserProfileRoleMembership } from 'common/services/api/UserProfileRoles/UserProfileRoles.thunk';
import { DeleteDialogContentProps, DrawerEntitiesNames } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { updateUserProfileTenant } from 'features/userProfileApps/helpers';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

export function UserProfileRolesDeleteDialog({
    tenant,
    itemId,
    customDeleteTitle,
    editingRow,
}: Readonly<DeleteDialogContentProps<{
    id: string,
    name: string,
    tenantId: string
}>>) {
    const { id, name } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const [processing, setProcessing] = useState<boolean>(false);
    const dispatch = useAppDispatch();
    const currentEntityType = useAppSelector(selectCurrentEntityType);
    const currentTenantId = useAppSelector(selectDeleteCurrentTenantId);
    const userDetails = useAppSelector(selectUserDetails);
    const { firstName, lastName, userId } = userDetails ?? {};

    const userProfileRoleDeleteCallback = useCallback(() => {
        if (!itemId) {
            return;
        }

        setProcessing(true);
        dispatch(deleteUserProfileRoleMembership({
            tenantId: tenant?.id ?? '',
            roleId: id ?? '',
            userId: userId ?? '',
        }))
            .unwrap()
            .then(() => {
                dispatch(setDialogOpen(false));
                dispatch(setCurrentEntityType());
                enqueueSnackbar('The Role has been removed from this User under this Tenant.', { variant: 'success' });
                updateUserProfileTenant({
                    tenantId: tenant?.id ?? '',
                    userId: userId ?? '',
                });
            })
            .catch(() => {
                enqueueSnackbar(
                    'There was an error while removing this Role from this User.' +
                    DEFAULT_ERROR_MESSAGE,
                    { variant: 'error' }
                );
            })
            .finally(()=> {
                setProcessing(false);
            });
    }, [itemId]);

    if (currentEntityType !== DrawerEntitiesNames.UserProfileRolesMembership || currentTenantId !== tenant?.id) {
        return null;
    }

    return  (
        <DeleteDialog deleteCallback={userProfileRoleDeleteCallback} processing={processing ?? false} customDeleteTitle={customDeleteTitle}>
            <DeleteDialogCustomField fieldName="Role Name" fieldValue={name} />
            <DeleteDialogCustomField fieldName="Name" fieldValue={`${firstName} ${lastName}`} />
            <DeleteDialogCustomField fieldName="Tenant Name" fieldValue={tenant?.name} />
        </DeleteDialog>
    );
}
